import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { getLanguage, setLanguage as persistLanguage } from '../utils/languageStorage';

interface LanguageContextType {
  language: 'ko' | 'en';
  toggleLanguage: () => void;
}

// 1. Create a Context
const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// 2. Create a Provider Component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  // 쿠키/localStorage에서 저장된 언어 불러오기, 없으면 한국어
  const [language, setLanguage] = useState<'ko' | 'en'>(() => getLanguage());

  // 언어 상태가 변경될 때 쿠키/localStorage에 저장
  useEffect(() => {
    persistLanguage(language);
  }, [language]);

  // 다른 탭/도메인에서 언어 변경 시 탭 전환하면 쿠키 재확인
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const newLang = getLanguage();
        setLanguage(prev => (prev !== newLang ? newLang : prev));
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const toggleLanguage = () => {
    setLanguage(prevLanguage => (prevLanguage === 'ko' ? 'en' : 'ko'));
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// 3. Create a custom hook to use the LanguageContext
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
